import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  Archive, ArchiveRestore, Check, Copy, Crown, Loader2, RefreshCw, Upload, X,
} from "lucide-react";
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Tabs, TabsList, TabsTrigger, TabsContent,
  ToggleGroup, ToggleGroupItem,
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
  cn,
} from "@nexus/ui";
import { MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX, type CurrencyCode, type NorthStarKpi } from "@nexus/contracts";
import type { AdminTenantSettingsData } from "@/features/identity-hub/api/identity-hub.api";
import { adminTenantSettingsQueryOptions, invalidateProductsForTenant } from "@/lib/queries";
import { orgColor } from "@/features/identity-hub/utils/iam.utils";
import {
  useUpdateTenant,
  useDeactivateTenant,
  useReactivateTenant,
  useAllIndustries,
  useAdminUpdateTenantSettings,
  useAdminTenantSettings,
  useTriggerTenantSync,
} from "@/features/identity-hub/hooks/useIdentityHub";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { OrgLogoMark } from "./OrgLogoMark";
import { OrgKpiSettingsTab } from "./OrgKpiSettingsTab";
import { IndustryMultiSelect } from "./IndustryMultiSelect.js";
import { industryLabel } from "./OrgCard";
import type { OrgItem } from "./OrgDrawer";
import { BRAND_COLORS } from "@/features/identity-hub/lib/brand-colors";
import { bothCatalogsUpdated, catalogSnapshot } from "@/features/identity-hub/lib/org-settings-sync-catalog";
import {
  decideInboxSync,
  emailsFromRows,
  newInboxRow,
  rowsFromSaved,
  sameEmails,
  savedInboxEmails,
  type InboxRow,
} from "@/features/identity-hub/lib/marketplace-inbox-rows";
import { presignTenantLogoUpload, confirmTenantLogoUpload } from "@/features/identity-hub/api/tenant-logo-upload.api";
import { putToSignedUrlWithProgress, stageMemoryPreflightUpload } from "@/features/preflight/api/uploads.api";
import { TENANT_LOGO_ACCEPT, getTenantLogoMaxBytes, isAllowedTenantLogoType } from "@/features/identity-hub/lib/tenant-logo-upload-constraints";
import { CURRENCY_DROPDOWN_OPTIONS } from "@/lib/currency-display";
import { toTitleCase } from "@/lib/title-case";
import { toast } from "sonner";

// ─── Constants ────────────────────────────────────────────────────────────────

const TENANT_TYPES = [
  { value: "ORGANIZATION", labelKey: "iam.orgDrawer.settings.tenantTypeOrganization", labelDefault: "Organization" },
  { value: "API", labelKey: "iam.orgDrawer.settings.tenantTypeApi", labelDefault: "API" },
] as const;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidUuid(s: string) { return UUID_RE.test(s); }

// Deliberately loose — the API's Zod `.email()` is the real gate. This only
// catches an obvious typo before the round trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(s: string) { return EMAIL_RE.test(s); }

const DESC_MAX = 500;
const ANIM_DURATION = 200;

type SettingsTab = "general" | "organization" | "kpis" | "danger";

// ─── Super-tenant lock overlay ─────────────────────────────────────────────────

function SuperTenantSettingsOverlay() {
  const { t } = useTranslation();

  return (
    <div
      className="absolute inset-0 flex items-center justify-center p-4"
      role="region"
      aria-label={t("iam.orgDrawer.settings.readOnlyOverlay.title", "Settings are locked")}
    >
      <div className="w-full max-w-[300px] rounded-xl border border-amber-500/30 bg-card/95 px-4 py-4 shadow-lg backdrop-blur-sm">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15">
          <Crown className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
        </div>
        <p className="text-sm font-semibold leading-snug text-foreground">
          {t("iam.orgDrawer.settings.readOnlyOverlay.title", "Settings are locked")}
        </p>
        <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
          {t(
            "iam.orgDrawer.settings.readOnlyOverlay.body",
            "Adge is the platform operator organization. Its profile is managed centrally and cannot be edited here.",
          )}
        </p>
        <ul className="mt-3 space-y-1.5 border-t border-border/60 pt-3 text-[11px] leading-relaxed text-muted-foreground">
          <li className="flex gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" aria-hidden />
            {t(
              "iam.orgDrawer.settings.readOnlyOverlay.reason1",
              "Protects cross-tenant administration and provisioning from accidental changes.",
            )}
          </li>
          <li className="flex gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" aria-hidden />
            {t(
              "iam.orgDrawer.settings.readOnlyOverlay.reason2",
              "Contact Adge engineering if a platform setting needs updating.",
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}

/** Dims + locks a settings region behind the super-tenant overlay. */
function LockableSection({ locked, children }: { locked: boolean; children: React.ReactNode }) {
  return (
    <div className={cn("relative", locked && "min-h-[320px]")}>
      <div
        className={cn(
          "flex flex-col gap-5",
          locked && "pointer-events-none select-none opacity-[0.35]",
        )}
        aria-hidden={locked}
      >
        {children}
      </div>
      {locked && <SuperTenantSettingsOverlay />}
    </div>
  );
}

// ─── OrgSettingsModal ───────────────────────────────────────────────────────────
//
// Centered modal for org settings, split into General / Organization / Danger tabs.
// Mounted only while open (conditional render at the parent) so every open — including
// same-org close→reopen — starts from fresh local state. A single Save in the footer
// commits a diff across all tabs.
//
// Sync lifecycle: closing the modal mid-sync unmounts it and stops the UI poll, but the
// backend Pub/Sub sync job keeps running. Reopening refetches admin settings and reflects
// any synced products. (See plan B-4 (c) / N-7 — the sync endpoint should be guarded
// server-side against concurrent runs.)

export interface OrgSettingsModalProps {
  org: OrgItem;
  isSuperTenant: boolean;
  onClose: () => void;
}

export function OrgSettingsModal({ org, isSuperTenant, onClose }: OrgSettingsModalProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);

  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState<SettingsTab>("general");
  const [idCopied, setIdCopied] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // ── Settings state ────────────────────────────────────────────────
  const [settingsName, setSettingsName] = useState(org.name ?? "");
  const [settingsType, setSettingsType] = useState(org.type === "API" ? "API" : "ORGANIZATION");
  const [settingsIndustryIndices, setSettingsIndustryIndices] = useState<string[]>(org.industryIndices ?? []);
  const [settingsDefaultIndustry, setSettingsDefaultIndustry] = useState<string | null>(org.defaultIndustry ?? null);
  const [settingsDefaultBrand, setSettingsDefaultBrand] = useState("");
  const [settingsColor, setSettingsColor] = useState<string | null>(org.brandColor?.trim() ?? null);
  const [settingsDescription, setSettingsDescription] = useState(org.description ?? "");
  const [settingsRetoolUserId, setSettingsRetoolUserId] = useState(org.retoolUserId ?? "");
  const [settingsRetoolTouched, setSettingsRetoolTouched] = useState(false);
  const [settingsCustomerPartition, setSettingsCustomerPartition] = useState("");
  const [settingsMarketplaceInboxRows, setSettingsMarketplaceInboxRows] = useState<InboxRow[]>([newInboxRow()]);
  const [settingsMarketplaceInboxTouched, setSettingsMarketplaceInboxTouched] = useState(false);
  const [settingsConversionsMonthly, setSettingsConversionsMonthly] = useState(0);
  const [settingsConversionsWeekly, setSettingsConversionsWeekly] = useState(0);
  const [settingsCurrency, setSettingsCurrency] = useState<CurrencyCode>("USD");
  const [settingsDefaultNorthStar, setSettingsDefaultNorthStar] = useState<NorthStarKpi | null>(null);
  const [conversionsSaved, setConversionsSaved] = useState(false);
  const [isSyncInFlight, setIsSyncInFlight] = useState(false);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const syncCatalogSnapshotRef = useRef(catalogSnapshot(undefined, undefined));
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ── Logo upload state ─────────────────────────────────────────────
  const [logoUploadId, setLogoUploadId] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState<number | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const updateTenantMut = useUpdateTenant();
  const deactivateTenantMut = useDeactivateTenant();
  const reactivateTenantMut = useReactivateTenant();
  const adminUpdateSettingsMut = useAdminUpdateTenantSettings();
  const triggerSyncMut = useTriggerTenantSync();
  const { data: adminSettings, isLoading: isLoadingAdminSettings } = useAdminTenantSettings(org.id);
  const { data: industries = [] } = useAllIndustries();

  const isArchived = !!org.deletedAt;

  // Seed customerPartition/conversions from the query cache immediately (avoids a flash),
  // then keep them in sync as the settings query resolves/updates.
  useEffect(() => {
    const cached = qc.getQueryData<AdminTenantSettingsData>(adminTenantSettingsQueryOptions(org.id).queryKey);
    if (cached) {
      setSettingsCustomerPartition(cached.customerPartition ?? "");
      setSettingsConversionsMonthly(cached.conversionsMonthly ?? 0);
      setSettingsConversionsWeekly(cached.conversionsWeekly ?? 0);
      setSettingsCurrency(cached.currency ?? "USD");
      setSettingsDefaultNorthStar(cached.defaultNorthStar ?? null);
      setSettingsDefaultIndustry(cached.defaultIndustry ?? null);
      setSettingsDefaultBrand(cached.defaultBrand ?? "");
      setSettingsMarketplaceInboxRows(rowsFromSaved(cached.marketplaceOrdersInboxEmails, cached.marketplaceOrdersInboxEmail));
    }
    // Run once on mount for this org.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (adminSettings) {
      setSettingsCustomerPartition(adminSettings.customerPartition ?? "");
      setSettingsConversionsMonthly(adminSettings.conversionsMonthly ?? 0);
      setSettingsConversionsWeekly(adminSettings.conversionsWeekly ?? 0);
      setSettingsCurrency(adminSettings.currency ?? "USD");
      setSettingsDefaultNorthStar(adminSettings.defaultNorthStar ?? null);
      setSettingsDefaultIndustry(adminSettings.defaultIndustry ?? null);
      setSettingsDefaultBrand(adminSettings.defaultBrand ?? "");
      const savedInbox = savedInboxEmails(
        adminSettings.marketplaceOrdersInboxEmails,
        adminSettings.marketplaceOrdersInboxEmail,
      );
      const decision = decideInboxSync(
        settingsMarketplaceInboxTouched,
        emailsFromRows(settingsMarketplaceInboxRows),
        savedInbox,
      );
      if (decision === "reseed") {
        setSettingsMarketplaceInboxRows(rowsFromSaved(adminSettings.marketplaceOrdersInboxEmails, adminSettings.marketplaceOrdersInboxEmail));
      } else if (decision === "mark-pristine") {
        setSettingsMarketplaceInboxTouched(false);
      }
    }
    // Inbox row identity is read at effect time; listing it would re-run this
    // effect on every keystroke and reset the other settings fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSettings?.customerPartition, adminSettings?.conversionsMonthly, adminSettings?.conversionsWeekly, adminSettings?.currency, adminSettings?.defaultNorthStar, adminSettings?.defaultIndustry, adminSettings?.defaultBrand, adminSettings?.marketplaceOrdersInboxEmails, adminSettings?.marketplaceOrdersInboxEmail, settingsMarketplaceInboxTouched]);

  // Stop sync polling helper
  const stopSyncPolling = () => {
    const wasPolling = syncPollRef.current !== undefined || syncTimeoutRef.current !== undefined;
    clearInterval(syncPollRef.current);
    clearTimeout(syncTimeoutRef.current);
    syncPollRef.current = undefined;
    syncTimeoutRef.current = undefined;
    if (wasPolling) {
      invalidateProductsForTenant(qc, org.id);
    }
  };

  // Detect catalog changes while sync is in flight
  useEffect(() => {
    if (!isSyncInFlight) return;
    const current = catalogSnapshot(adminSettings?.productTypes, adminSettings?.products);
    if (bothCatalogsUpdated(current, syncCatalogSnapshotRef.current)) {
      setIsSyncInFlight(false);
      stopSyncPolling();
    }
  }, [isSyncInFlight, adminSettings?.productTypes, adminSettings?.products]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopSyncPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived / validation ──────────────────────────────────────────
  const isLogoUploading = logoUploadProgress !== null;
  const displayLogoUrl = logoRemoved ? null : (logoPreviewUrl ?? (org.logoUrl ?? null));
  const accentColor = settingsColor ?? org.brandColor ?? orgColor(org.name);
  const descOver = settingsDescription.length > DESC_MAX;
  const productTypes = adminSettings?.productTypes ?? [];
  const skuProducts = adminSettings?.products ?? [];
  const focusedSkus = skuProducts.filter((p) => p.isFocused);
  const unfocusedSkuCount = skuProducts.filter((p) => !p.isFocused).length;
  const retoolError = settingsRetoolUserId && !isValidUuid(settingsRetoolUserId)
    ? t("iam.validation.invalidUuid")
    : null;
  const marketplaceInboxError = settingsMarketplaceInboxRows.some((row) => {
    const trimmed = row.email.trim();
    return trimmed.length > 0 && !isValidEmail(trimmed);
  })
    ? t("iam.validation.invalidEmail", "Enter a valid email address")
    : null;
  const partitionIsEmpty = settingsCustomerPartition.trim().length === 0;
  const isSyncing = isSyncInFlight || triggerSyncMut.isPending;
  const isSavingPartition = adminUpdateSettingsMut.isPending;

  const canSave =
    !isSuperTenant &&
    settingsName.trim().length > 0 &&
    !descOver &&
    !retoolError &&
    !marketplaceInboxError &&
    !updateTenantMut.isPending &&
    !adminUpdateSettingsMut.isPending &&
    !isLogoUploading;

  // ── Close ─────────────────────────────────────────────────────────
  const requestClose = () => {
    setOpen(false);
    window.setTimeout(onClose, ANIM_DURATION);
  };

  const handleCopyId = () => {
    void navigator.clipboard.writeText(org.id).then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1800);
    });
  };

  // ── Logo handlers ─────────────────────────────────────────────────
  const handleLogoFileChange = async (file: File | undefined) => {
    if (!file || !activeTenantId) return;

    if (!isAllowedTenantLogoType(file.type)) {
      toast.error(t("iam.orgDrawer.settings.logoInvalidType", "Use PNG, JPEG, or WebP"));
      return;
    }
    const maxBytes = getTenantLogoMaxBytes(file.type);
    if (maxBytes != null && file.size > maxBytes) {
      toast.error(t("iam.orgDrawer.settings.logoTooLarge", "Logo file is too large"));
      return;
    }

    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setLogoRemoved(false);
    setLogoUploadProgress(0);

    try {
      const presign = await presignTenantLogoUpload({
        tenantId: org.id,
        contentType: file.type,
        sizeBytes: file.size,
        originalName: file.name,
      });

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        await stageMemoryPreflightUpload({
          tenantId: org.id,
          uploadId: presign.uploadId,
          contentType: file.type,
          file,
        });
        setLogoUploadProgress(100);
      } else {
        await putToSignedUrlWithProgress(presign.uploadUrl, file, presign.requiredHeaders, {
          onProgress: setLogoUploadProgress,
        });
      }

      const confirmed = await confirmTenantLogoUpload({
        tenantId: org.id,
        uploadId: presign.uploadId,
      });

      setLogoUploadId(confirmed.uploadId);
      toast.success(t("iam.orgDrawer.settings.logoUploaded", "Logo uploaded"));
    } catch {
      toast.error(t("iam.orgDrawer.settings.logoUploadFailed", "Logo upload failed"));
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl(null);
    } finally {
      setLogoUploadProgress(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleLogoRemove = () => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(null);
    setLogoUploadId(null);
    setLogoRemoved(true);
  };

  // ── Upstream sync ─────────────────────────────────────────────────
  const handleTriggerSync = () => {
    const trimmedPartition = settingsCustomerPartition.trim();
    if (trimmedPartition.length === 0) return;

    const currentPartition = adminSettings?.customerPartition ?? "";
    const partitionIsDirty = trimmedPartition !== currentPartition;

    const startSyncWithPolling = () => {
      syncCatalogSnapshotRef.current = catalogSnapshot(
        adminSettings?.productTypes,
        adminSettings?.products,
      );
      setIsSyncInFlight(true);

      triggerSyncMut.mutate(org.id, {
        onSuccess: () => {
          syncPollRef.current = setInterval(() => {
            void qc.invalidateQueries({ queryKey: ["identity-hub", "tenant-settings", org.id] });
          }, 3000);

          syncTimeoutRef.current = setTimeout(() => {
            setIsSyncInFlight(false);
            stopSyncPolling();
          }, 30_000);
        },
        onError: () => {
          setIsSyncInFlight(false);
        },
      });
    };

    if (partitionIsDirty) {
      adminUpdateSettingsMut.mutate(
        { tenantId: org.id, data: { customerPartition: trimmedPartition } },
        { onSuccess: () => startSyncWithPolling() },
      );
    } else {
      startSyncWithPolling();
    }
  };

  // ── Save (diff-based) ─────────────────────────────────────────────
  const handleSave = () => {
    setSettingsRetoolTouched(true);
    setSettingsMarketplaceInboxTouched(true);
    if (!canSave) return;

    const trimmedName = settingsName.trim();
    const trimmedDesc = settingsDescription.trim();
    const nextDescription = trimmedDesc.length > 0 ? trimmedDesc : null;
    const currentDescription = org.description ?? null;
    const currentType = org.type === "API" ? "API" : "ORGANIZATION";
    const currentRetoolUserId = org.retoolUserId ?? "";

    const changes: Record<string, unknown> = {};
    if (trimmedName !== org.name)               changes.name = trimmedName;
    if (nextDescription !== currentDescription) changes.description = nextDescription;
    const currentBrandColor = org.brandColor?.trim() ?? null;
    if (settingsColor !== currentBrandColor)    changes.brandColor = settingsColor;
    if (settingsType !== currentType)           changes.type = settingsType;
    if (settingsRetoolUserId && settingsRetoolUserId !== currentRetoolUserId) changes.retoolUserId = settingsRetoolUserId;
    const prevIndices = [...(org.industryIndices ?? [])].sort().join(',');
    const nextIndices = [...settingsIndustryIndices].sort().join(',');
    if (nextIndices !== prevIndices)            changes.industryIndices = settingsIndustryIndices;
    // Logo: new upload ID, or explicit removal
    if (logoUploadId !== null) changes.logoUploadId = logoUploadId;
    else if (logoRemoved) { changes.logoUrl = null; changes.logoUploadId = null; }

    // If the selected default is no longer in the assigned industries, clear it.
    const effectiveDefault = settingsIndustryIndices.includes(settingsDefaultIndustry ?? "")
      ? settingsDefaultIndustry
      : settingsIndustryIndices.length === 1 ? settingsIndustryIndices[0]! : null;

    const defaultChanged = effectiveDefault !== (adminSettings?.defaultIndustry ?? null);

    const trimmedBrand = settingsDefaultBrand.trim();
    const nextBrand = trimmedBrand.length > 0 ? trimmedBrand : null;
    const brandChanged = nextBrand !== (adminSettings?.defaultBrand ?? null);

    const trimmedPartition = settingsCustomerPartition.trim();
    const nextPartition = trimmedPartition.length > 0 ? trimmedPartition : null;
    const currentPartition = adminSettings?.customerPartition ?? null;
    const partitionChanged = nextPartition !== currentPartition;

    const nextInbox = emailsFromRows(settingsMarketplaceInboxRows);
    const currentInbox = savedInboxEmails(
      adminSettings?.marketplaceOrdersInboxEmails,
      adminSettings?.marketplaceOrdersInboxEmail,
    );
    const inboxChanged = !sameEmails(nextInbox, currentInbox);

    const conversionsMonthlyChanged = settingsConversionsMonthly !== (adminSettings?.conversionsMonthly ?? 0);
    const conversionsWeeklyChanged  = settingsConversionsWeekly  !== (adminSettings?.conversionsWeekly  ?? 0);
    const currencyChanged = settingsCurrency !== (adminSettings?.currency ?? "USD");
    const northStarChanged = settingsDefaultNorthStar !== (adminSettings?.defaultNorthStar ?? null);

    const hasTenantChanges = Object.keys(changes).length > 0;
    const hasSettingsChanges = defaultChanged || brandChanged || partitionChanged || conversionsMonthlyChanged || conversionsWeeklyChanged || currencyChanged || northStarChanged || inboxChanged;

    if (!hasTenantChanges && !hasSettingsChanges) {
      setSettingsRetoolTouched(false);
      return;
    }

    if (hasTenantChanges) {
      updateTenantMut.mutate(
        { tenantId: org.id, changes },
        {
          onSuccess: () => {
            setSettingsName(trimmedName);
            setSettingsDescription(trimmedDesc);
            setSettingsRetoolTouched(false);
          },
        },
      );
    }

    if (hasSettingsChanges) {
      const settingsData: { defaultIndustry?: string | null; defaultBrand?: string | null; customerPartition?: string | null; conversionsMonthly?: number; conversionsWeekly?: number; currency?: CurrencyCode; defaultNorthStar?: NorthStarKpi | null; marketplaceOrdersInboxEmails?: string[] } = {};
      if (defaultChanged)            settingsData.defaultIndustry    = effectiveDefault;
      if (brandChanged)              settingsData.defaultBrand       = nextBrand;
      if (partitionChanged)          settingsData.customerPartition  = nextPartition;
      if (conversionsMonthlyChanged) settingsData.conversionsMonthly = settingsConversionsMonthly;
      if (conversionsWeeklyChanged)  settingsData.conversionsWeekly  = settingsConversionsWeekly;
      if (currencyChanged)           settingsData.currency            = settingsCurrency;
      if (northStarChanged)          settingsData.defaultNorthStar   = settingsDefaultNorthStar;
      if (inboxChanged)              settingsData.marketplaceOrdersInboxEmails = nextInbox;

      adminUpdateSettingsMut.mutate(
        { tenantId: org.id, data: settingsData, showToast: !hasTenantChanges },
        {
          onSuccess: () => {
            if (defaultChanged) setSettingsDefaultIndustry(effectiveDefault);
            if (brandChanged) setSettingsDefaultBrand(trimmedBrand);
            if (partitionChanged) setSettingsCustomerPartition(trimmedPartition);
            if (inboxChanged) {
              setSettingsMarketplaceInboxRows(rowsFromSaved(nextInbox, null));
            }
            if (currencyChanged) setSettingsCurrency(settingsCurrency);
            if (northStarChanged) setSettingsDefaultNorthStar(settingsDefaultNorthStar);
            if (conversionsMonthlyChanged || conversionsWeeklyChanged) {
              setConversionsSaved(true);
              setTimeout(() => setConversionsSaved(false), 2000);
            }
          },
        },
      );
    }
  };

  const handleArchive = async () => {
    await deactivateTenantMut.mutateAsync({ tenantId: org.id });
    requestClose();
  };

  const handleRestore = async () => {
    await reactivateTenantMut.mutateAsync({ tenantId: org.id });
    // Stay open — user usually wants to keep editing the just-restored org.
  };

  const isSaving = updateTenantMut.isPending || adminUpdateSettingsMut.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
      {/* Hidden logo file input — triggered from the Logo field */}
      <input
        ref={logoInputRef}
        type="file"
        accept={TENANT_LOGO_ACCEPT}
        className="sr-only"
        disabled={isLogoUploading || isSuperTenant}
        onChange={(e) => void handleLogoFileChange(e.target.files?.[0])}
      />

      <DialogContent variant="form" className="flex h-[70vh] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 p-5 pb-4">
          <OrgLogoMark
            name={org.name}
            logoUrl={displayLogoUrl}
            accent={accentColor}
            isSuperTenant={isSuperTenant}
            size={36}
          />
          <div className="min-w-0">
            <DialogTitle className="truncate text-[15px] font-semibold">{org.name}</DialogTitle>
            <DialogDescription>{industryLabel(org.type)} · {t("iam.orgSettings.subtitle", "Organization settings")}</DialogDescription>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as SettingsTab)} className="min-h-0 flex-1 gap-0 overflow-hidden">
          <TabsList
            variant="line"
            className={cn(
              "w-full justify-start px-5",
              // Recolor the shared active-underline indicator red while the Danger tab is active.
              tab === "danger" && "[&>[aria-hidden]]:bg-destructive",
            )}
          >
            <TabsTrigger
              value="general"
              data-testid="org-settings-tab-general"
              badge={descOver ? <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden /> : undefined}
            >
              {t("iam.orgSettings.tabs.general", "General")}
            </TabsTrigger>
            <TabsTrigger
              value="organization"
              data-testid="org-settings-tab-organization"
              badge={settingsRetoolTouched && retoolError ? <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden /> : undefined}
            >
              {t("iam.orgSettings.tabs.organization", "Organization")}
            </TabsTrigger>
            <TabsTrigger value="kpis" data-testid="org-settings-tab-kpis">
              {t("iam.orgSettings.tabs.orgKpis", "Org KPIs")}
            </TabsTrigger>
            {!isSuperTenant && (
              <TabsTrigger
                value="danger"
                data-testid="org-settings-tab-danger"
                className="text-destructive/80 hover:text-destructive data-[state=active]:text-destructive"
              >
                {t("iam.orgSettings.tabs.danger", "Danger zone")}
              </TabsTrigger>
            )}
          </TabsList>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {/* ── General ─────────────────────────────────────── */}
            <TabsContent value="general" className="mt-0 flex flex-col gap-5">
              {/* Tenant ID — always accessible for API reference */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  {t("iam.orgDrawer.settings.tenantId", "Tenant ID")}
                </label>
                <div className="group/tid relative flex h-8 items-center overflow-hidden rounded-md border border-border bg-muted/40">
                  <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap px-3 font-mono text-xs text-muted-foreground">
                    {org.id}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyId}
                    aria-label={t("iam.orgDrawer.settings.copyTenantIdAria", "Copy tenant ID")}
                    className={cn(
                      "flex h-full shrink-0 items-center gap-1 border-l border-border px-2.5 text-[11px] font-medium transition-colors",
                      idCopied
                        ? "bg-success-subtle text-success-subtle-foreground"
                        : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {idCopied ? (
                      <><Check className="h-3 w-3" />{t("iam.orgDrawer.settings.copied", "Copied!")}</>
                    ) : (
                      <><Copy className="h-3 w-3" />{t("iam.orgDrawer.settings.copy", "Copy")}</>
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t("iam.orgDrawer.settings.tenantIdHint", "Read-only · reference this org in API calls")}
                </p>
              </div>

              <LockableSection locked={isSuperTenant}>
                {/* Name */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t("iam.orgDrawer.settings.organizationNameLabel", "Organization name")} <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={settingsName}
                    onChange={(e) => setSettingsName(e.target.value)}
                    disabled={isSuperTenant}
                    maxLength={255}
                    className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                {/* Type */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t("iam.orgDrawer.settings.typeLabel", "Type")} <span className="text-destructive">*</span>
                  </label>
                  <ToggleGroup
                    type="single"
                    value={settingsType}
                    onValueChange={(v) => v && setSettingsType(v)}
                    disabled={isSuperTenant}
                    size="sm"
                    className="h-auto w-fit p-0.5"
                  >
                    {TENANT_TYPES.map((tt) => (
                      <ToggleGroupItem
                        key={tt.value}
                        value={tt.value}
                        className="h-7 flex-1 justify-center px-2.5 text-[11px]"
                      >
                        {t(tt.labelKey, tt.labelDefault)}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>

                {/* Logo */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t("iam.orgDrawer.settings.logoLabel", "Logo")}
                  </label>
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      <OrgLogoMark
                        name={org.name}
                        logoUrl={displayLogoUrl}
                        accent={settingsColor ?? orgColor(org.name)}
                        size={56}
                      />
                      {isLogoUploading && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80">
                          <span className="text-[10px] font-semibold tabular-nums text-foreground">
                            {logoUploadProgress ?? 0}%
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          disabled={isLogoUploading || isSuperTenant}
                          onClick={() => logoInputRef.current?.click()}
                          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Upload className="h-3 w-3" />
                          {isLogoUploading
                            ? t("iam.orgDrawer.settings.logoUploading", "Uploading…")
                            : t("iam.orgDrawer.settings.logoUpload", "Upload logo")}
                        </button>
                        {(displayLogoUrl ?? logoUploadId) && !isLogoUploading && (
                          <button
                            type="button"
                            disabled={isSuperTenant}
                            onClick={handleLogoRemove}
                            className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <X className="h-3 w-3" />
                            {t("iam.orgDrawer.settings.logoRemove", "Remove")}
                          </button>
                        )}
                      </div>
                      {isLogoUploading && (
                        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-200"
                            style={{ width: `${logoUploadProgress ?? 0}%` }}
                          />
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground">
                        {t("iam.orgDrawer.settings.logoHint", "PNG, JPEG, WebP · max 2 MB")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Brand color */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.brandColorLabel", "Brand color")}</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      aria-label={t("iam.orgDrawer.settings.autoAccentAria", "Auto accent")}
                      disabled={isSuperTenant}
                      onClick={() => setSettingsColor(null)}
                      className={cn(
                        "relative h-8 w-8 rounded-lg border border-border/50 transition-all disabled:cursor-not-allowed disabled:opacity-50",
                        settingsColor === null && "ring-2 ring-offset-2 ring-ring",
                      )}
                      style={{ background: orgColor(org.name) }}
                    >
                      {settingsColor === null && (
                        <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow-sm" />
                      )}
                    </button>
                    {BRAND_COLORS.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        aria-label={c.label}
                        disabled={isSuperTenant}
                        onClick={() => setSettingsColor(c.value)}
                        className={cn(
                          "relative h-8 w-8 rounded-lg transition-all disabled:cursor-not-allowed disabled:opacity-50",
                          settingsColor === c.value && "ring-2 ring-offset-2 ring-ring",
                        )}
                        style={{ backgroundColor: c.value }}
                      >
                        {settingsColor === c.value && (
                          <Check className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow-sm" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.descriptionLabel", "Description")}</label>
                    <span className={cn("text-[10px] tabular-nums", descOver ? "text-destructive" : "text-muted-foreground/60")}>
                      {settingsDescription.length}/{DESC_MAX}
                    </span>
                  </div>
                  <textarea
                    value={settingsDescription}
                    onChange={(e) => setSettingsDescription(e.target.value)}
                    disabled={isSuperTenant}
                    maxLength={DESC_MAX}
                    rows={5}
                    placeholder={t("iam.orgDrawer.settings.descriptionPlaceholder", "Short note about this tenant…")}
                    className={cn(
                      "max-h-40 min-h-[7.5rem] resize-none overflow-y-auto rounded-md border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
                      descOver
                        ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                        : "border-border focus:border-ring focus:ring-ring/40",
                    )}
                  />
                  {descOver && (
                    <p className="text-[11px] text-destructive">{t("iam.validation.descTooLong")}</p>
                  )}
                </div>
              </LockableSection>
            </TabsContent>

            {/* ── Organization ────────────────────────────────── */}
            <TabsContent value="organization" className="mt-0 flex flex-col gap-5">
              <LockableSection locked={isSuperTenant}>
                {/* Industries */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.industriesLabel", "Industries")}</label>
                  <IndustryMultiSelect
                    selected={settingsIndustryIndices}
                    onChange={(next) => {
                      setSettingsIndustryIndices(next);
                      // If the current default is no longer in the selection, clear it
                      if (settingsDefaultIndustry && !next.includes(settingsDefaultIndustry)) {
                        setSettingsDefaultIndustry(next.length === 1 ? next[0]! : null);
                      }
                    }}
                    industries={industries}
                    disabled={isSuperTenant}
                    placeholder={t("iam.orgDrawer.settings.industriesPlaceholder", "Select industries…")}
                  />
                </div>

                {/* Default industry — only shown when 2+ industries are assigned */}
                {settingsIndustryIndices.length >= 2 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                      {t("iam.orgDrawer.settings.defaultIndustryLabel", "Default industry")}
                      <span className="ml-1 font-normal text-muted-foreground/70">{t("iam.orgDrawer.settings.defaultIndustryHint", "— pre-selected in Create Opportunity Set")}</span>
                    </label>
                    <select
                      value={settingsDefaultIndustry ?? ""}
                      onChange={(e) => setSettingsDefaultIndustry(e.target.value || null)}
                      disabled={isSuperTenant}
                      className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">{t("iam.shared.none", "None")}</option>
                      {industries
                        .filter((ind) => settingsIndustryIndices.includes(ind.index))
                        .map((ind) => (
                          <option key={ind.index} value={ind.index}>{ind.title}</option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t("iam.orgDrawer.settings.defaultBrandLabel", "Default brand")}
                    <span className="ml-1 font-normal text-muted-foreground/70">{t("iam.orgDrawer.settings.defaultBrandHint", "Brand name analysed in Pre Flight (as it appears in the creative)")}</span>
                  </label>
                  {isLoadingAdminSettings ? (
                    <div className="h-8 rounded-md border border-border bg-muted/40 animate-pulse" />
                  ) : (
                    <input
                      type="text"
                      data-testid="org-settings-default-brand"
                      placeholder={t("iam.orgDrawer.settings.defaultBrandPlaceholder", "e.g. Vodafone")}
                      value={settingsDefaultBrand}
                      onChange={(e) => setSettingsDefaultBrand(e.target.value)}
                      disabled={isSuperTenant}
                      className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.displayCurrencyLabel", "Display currency")}</label>
                  <select
                    data-testid="org-settings-currency"
                    value={settingsCurrency}
                    onChange={(e) => setSettingsCurrency(e.target.value as CurrencyCode)}
                    disabled={isSuperTenant}
                    className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {CURRENCY_DROPDOWN_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>{option.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground">{t("iam.orgDrawer.settings.displayCurrencyHint", "Changes symbol display only. Values are not converted.")}</p>
                </div>

                {/* Default NorthStar KPI */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.defaultNorthStarLabel", "Default NorthStar KPI")}</label>
                  <select
                    data-testid="org-settings-default-north-star"
                    value={settingsDefaultNorthStar ?? ""}
                    onChange={(e) => setSettingsDefaultNorthStar(e.target.value === "" ? null : e.target.value as NorthStarKpi)}
                    disabled={isSuperTenant}
                    className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">{t("iam.orgDrawer.settings.defaultNorthStarNone", "None (use user preference)")}</option>
                    <option value="roas">{t("iam.orgDrawer.settings.defaultNorthStarRoas", "ROAS")}</option>
                    <option value="cpa">{t("iam.orgDrawer.settings.defaultNorthStarCpa", "CPA")}</option>
                    <option value="custom">{t("iam.orgDrawer.settings.defaultNorthStarCustom", "Custom KPI")}</option>
                  </select>
                  <p className="text-[11px] text-muted-foreground">{t("iam.orgDrawer.settings.defaultNorthStarHint", "Sets the default NorthStar for new users on this tenant.")}</p>
                </div>

                {/* Retool user ID */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.retoolUserIdLabel", "Retool user ID")}</label>
                  <input
                    type="text"
                    placeholder={t("iam.orgDrawer.settings.retoolUserIdPlaceholder", "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx")}
                    value={settingsRetoolUserId}
                    onChange={(e) => setSettingsRetoolUserId(e.target.value.trim())}
                    onBlur={() => setSettingsRetoolTouched(true)}
                    disabled={isSuperTenant}
                    className={cn(
                      "h-8 rounded-md border bg-background px-3 font-mono text-xs text-foreground outline-none placeholder:font-sans placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50",
                      settingsRetoolTouched && retoolError ? "border-destructive focus:border-destructive" : "border-border focus:border-ring",
                    )}
                  />
                  {settingsRetoolTouched && retoolError
                    ? <p className="text-[11px] text-destructive">{retoolError}</p>
                    : <p className="text-[11px] text-muted-foreground">{t("iam.orgDrawer.settings.retoolUserIdOptionalHint", "Optional · must be a valid UUID if provided")}</p>
                  }
                </div>

                {/* Marketplace orders inbox */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.marketplaceOrdersInboxLabel", "Marketplace orders inbox")}</label>
                  {isLoadingAdminSettings ? (
                    <div className="h-8 rounded-md border border-border bg-muted/40 animate-pulse" />
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {settingsMarketplaceInboxRows.map((row, index) => {
                        const trimmed = row.email.trim();
                        const rowInvalid = settingsMarketplaceInboxTouched && trimmed.length > 0 && !isValidEmail(trimmed);
                        return (
                          <div key={row.uid} className="flex items-center gap-1.5">
                            <input
                              type="email"
                              data-testid={index === 0 ? "org-settings-marketplace-orders-inbox" : `org-settings-marketplace-orders-inbox-${index}`}
                              placeholder={t("iam.orgDrawer.settings.marketplaceOrdersInboxPlaceholder", "orders@company.com")}
                              value={row.email}
                              onChange={(e) => {
                                const value = e.target.value;
                                setSettingsMarketplaceInboxTouched(true);
                                setSettingsMarketplaceInboxRows((prev) =>
                                  prev.map((item) => (item.uid === row.uid ? { ...item, email: value } : item)),
                                );
                              }}
                              onBlur={() => setSettingsMarketplaceInboxTouched(true)}
                              disabled={isSuperTenant}
                              aria-label={t("iam.orgDrawer.settings.marketplaceOrdersInboxRowAria", "Orders inbox {{index}}", { index: index + 1 })}
                              aria-invalid={rowInvalid ? true : undefined}
                              className={cn(
                                "h-8 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50",
                                rowInvalid ? "border-destructive focus:border-destructive" : "border-border focus:border-ring",
                              )}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0 min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
                              iconStart="trash-2"
                              disabled={isSuperTenant || settingsMarketplaceInboxRows.length <= 1}
                              onClick={() => {
                                setSettingsMarketplaceInboxTouched(true);
                                setSettingsMarketplaceInboxRows((prev) =>
                                  prev.length <= 1 ? [newInboxRow()] : prev.filter((item) => item.uid !== row.uid),
                                );
                              }}
                              aria-label={t("iam.orgDrawer.settings.marketplaceOrdersInboxRemove", "Remove {{email}}", {
                                email: row.email || t("iam.orgDrawer.settings.marketplaceOrdersInboxLabel", "Marketplace orders inbox"),
                              })}
                            />
                          </div>
                        );
                      })}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="self-start"
                        iconStart="plus"
                        disabled={isSuperTenant || settingsMarketplaceInboxRows.length >= MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX}
                        onClick={() => {
                          setSettingsMarketplaceInboxTouched(true);
                          setSettingsMarketplaceInboxRows((prev) =>
                            prev.length >= MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX ? prev : [...prev, newInboxRow()],
                          );
                        }}
                      >
                        {t("iam.orgDrawer.settings.marketplaceOrdersInboxAdd", "Add email")}
                      </Button>
                    </div>
                  )}
                  {settingsMarketplaceInboxTouched && marketplaceInboxError
                    ? <p className="text-[11px] text-destructive">{marketplaceInboxError}</p>
                    : <p className="text-[11px] text-muted-foreground">{t("iam.orgDrawer.settings.marketplaceOrdersInboxHint", "Copied on every marketplace order email for this org. Leave empty to copy the person placing the order.")}</p>
                  }
                </div>

                {/* Upstream sync */}
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <p className="mb-3 text-xs font-semibold">{t("iam.orgDrawer.settings.upstreamSync", "Upstream sync")}</p>

                  {/* Customer Partition */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.customerPartitionLabel", "Customer Partition")}</label>
                    {isLoadingAdminSettings ? (
                      <div className="h-8 rounded-md border border-border bg-muted/40 animate-pulse" />
                    ) : (
                      <input
                        type="text"
                        placeholder={t("iam.orgDrawer.settings.customerPartitionPlaceholder", "e.g. Acme Corp")}
                        value={settingsCustomerPartition}
                        onChange={(e) => setSettingsCustomerPartition(e.target.value)}
                        disabled={isSuperTenant}
                        className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    )}
                  </div>

                  {/* Conversions */}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.conversionsLabel", "Conversions")}</span>
                    {conversionsSaved && (
                      <span className="animate-in fade-in text-[11px] text-emerald-600 duration-200">{t("iam.orgDrawer.settings.conversionsSaved", "Saved")}</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.weeklyConversionsLabel", "Weekly Conversions")}</label>
                      {isLoadingAdminSettings ? (
                        <div className="h-8 rounded-md border border-border bg-muted/40 animate-pulse" />
                      ) : (
                        <input
                          type="number"
                          min={0}
                          value={settingsConversionsWeekly}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setSettingsConversionsWeekly(isNaN(v) ? 0 : Math.max(0, v));
                          }}
                          disabled={isSuperTenant}
                          className="h-8 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <label className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.monthlyConversionsLabel", "Monthly Conversions")}</label>
                      {isLoadingAdminSettings ? (
                        <div className="h-8 rounded-md border border-border bg-muted/40 animate-pulse" />
                      ) : (
                        <input
                          type="number"
                          min={0}
                          value={settingsConversionsMonthly}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setSettingsConversionsMonthly(isNaN(v) ? 0 : Math.max(0, v));
                          }}
                          disabled={isSuperTenant}
                          className="h-8 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      )}
                    </div>
                  </div>

                  {/* Sync button */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 mt-3"
                            disabled={isSuperTenant || isSyncing || isSavingPartition || partitionIsEmpty}
                            onClick={handleTriggerSync}
                          >
                            {isSyncing || isSavingPartition ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                            {isSavingPartition ? t("iam.orgDrawer.settings.savingPartition", "Saving partition…") : isSyncing ? t("iam.orgDrawer.settings.syncing", "Syncing…") : t("iam.orgDrawer.settings.syncWithBq", "Sync with BQ")}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {partitionIsEmpty && !isSuperTenant && (
                        <TooltipContent side="bottom">
                          {t("iam.orgDrawer.settings.setPartitionFirst", "Set a Customer Partition first")}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>

                  {/* Product types, then SKU pills */}
                  <div className="mt-3 flex flex-col gap-3">
                    {isLoadingAdminSettings ? (
                      <div className="h-10 rounded-md border border-border bg-muted/40 animate-pulse" />
                    ) : (
                      <>
                        <section className="flex flex-col gap-1.5">
                          <h3 className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.tenantProductTypesLabel", "Product Types")}</h3>
                          {productTypes.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {productTypes.map((p) => (
                                <span
                                  key={`type-${p.name}`}
                                  className="rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground"
                                >
                                  {toTitleCase(p.name)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[11px] text-muted-foreground italic">{t("iam.orgDrawer.settings.noProductsSynced", "No products synced yet")}</p>
                          )}
                        </section>
                        {skuProducts.length > 0 && (
                          <section className="flex flex-col gap-1.5 border-t border-border pt-3">
                            <h3 className="text-xs font-medium text-muted-foreground">{t("iam.orgDrawer.settings.tenantProductsLabel", "Tenant Products")}</h3>
                            <div className="flex flex-wrap gap-1.5">
                              {focusedSkus.map((p) => (
                                <span
                                  key={`sku-${p.name}`}
                                  className="rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground"
                                >
                                  {p.name}
                                </span>
                              ))}
                            </div>
                            {unfocusedSkuCount > 0 ? (
                              <p className="text-[11px] text-muted-foreground">
                                {t("iam.orgDrawer.settings.moreProducts", "…{{count}} more products", { count: unfocusedSkuCount })}
                              </p>
                            ) : null}
                          </section>
                        )}
                      </>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      {t("iam.orgDrawer.settings.syncHint", "Syncs products and industries from BigQuery for this tenant")}
                    </p>
                  </div>
                </div>
              </LockableSection>
            </TabsContent>

            <TabsContent value="kpis" className="mt-0">
              <LockableSection locked={isSuperTenant}>
                <OrgKpiSettingsTab tenantId={org.id} locked={isSuperTenant || isArchived} />
              </LockableSection>
            </TabsContent>

            {/* ── Danger zone ─────────────────────────────────── */}
            {!isSuperTenant && (
              <TabsContent value="danger" className="mt-0 flex flex-col gap-5">
                {isArchived ? (
                  <div className="rounded-lg border border-border bg-muted/30 p-3">
                    <p className="mb-1 text-xs font-semibold">{t("iam.orgDrawer.restoreZone.title", "Archived organization")}</p>
                    <p className="mb-2 text-[11px] text-muted-foreground">
                      {t(
                        "iam.orgDrawer.restoreZone.body",
                        "This organization is archived and hidden from the active list. Restore it to make it visible and editable again.",
                      )}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={reactivateTenantMut.isPending}
                      onClick={() => void handleRestore()}
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                      {reactivateTenantMut.isPending
                        ? t("iam.orgDrawer.restoreZone.pending", "Restoring…")
                        : t("iam.orgDrawer.restoreZone.confirm", "Restore organization")}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="mb-2 text-xs font-semibold text-destructive">{t("iam.orgDrawer.settings.dangerZone", "Danger zone")}</p>
                    {confirmArchive ? (
                      <div className="flex flex-col gap-2.5">
                        <p className="text-[11px] text-muted-foreground">
                          {t("iam.orgDrawer.settings.archiveWarning", "This will archive the organization and remove all active access. You can restore it later from the archived list.")}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            disabled={deactivateTenantMut.isPending}
                            onClick={() => void handleArchive()}
                            variant="destructive"
                            size="sm"
                            className="h-6 px-2.5 text-[11px]"
                          >
                            {deactivateTenantMut.isPending ? t("iam.orgDrawer.settings.archiving", "Archiving…") : t("iam.orgDrawer.settings.confirmArchive", "Confirm archive")}
                          </Button>
                          <button
                            type="button"
                            onClick={() => setConfirmArchive(false)}
                            className="text-[11px] text-muted-foreground hover:text-foreground"
                          >
                            {t("common.cancel", "Cancel")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setConfirmArchive(true)}
                      >
                        <Archive className="h-3.5 w-3.5" />
                        {t("iam.orgDrawer.settings.archiveOrganization", "Archive organization")}
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>
            )}
          </div>
        </Tabs>

        <DialogFooter className="!mx-0 !mb-0 items-center">
          {!isSuperTenant && !canSave && !isSaving && (
            <span className="mr-auto text-[11px] text-muted-foreground">
              {settingsName.trim().length === 0
                ? t("iam.orgSettings.saveHint.name", "Organization name is required")
                : descOver
                  ? t("iam.orgSettings.saveHint.desc", "Description is too long")
                  : retoolError
                    ? t("iam.orgSettings.saveHint.retool", "Fix the Retool user ID on the Organization tab")
                    : marketplaceInboxError
                      ? t("iam.orgSettings.saveHint.inbox", "Fix the Marketplace orders inbox on the Organization tab")
                      : ""}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={requestClose}>
            {t("common.close", "Close")}
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            data-testid="org-settings-save"
            disabled={isSuperTenant || isArchived || !canSave}
            onClick={handleSave}
          >
            {isSaving ? t("iam.orgDrawer.settings.saving", "Saving…") : t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
